import * as React from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem", color: "#fff" }} />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "#39484E",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export default function Faqs() {
  const [expanded, setExpanded] = React.useState("");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Box margin={{ xs: "25% 0%", sm: "8% 0%" }}>
      <Typography
        variant={"h3"}
        fontWeight={700}
        textAlign={"left"}
        color={"#39484E"}
        paddingBottom={"2%"}
      >
        Frequent Asked Questions
      </Typography>
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                How does AIR Fund define Secondary Cities?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                A Secondary City is a city with unrealized potential where
                existing conditions — geographic position, human capital,
                economic activity, or natural endowments — suggest a
                significantly higher level of development than is currently
                observed, and where targeted investment could unlock that gap.
              </Typography>
              <Typography color={"#000"} paddingTop={"2%"}>
                It is neither a state capital (like Abuja in Nigeria or
                Yammousokro in Ivory Coast), nor an established commercial hub
                (like Dakar in Senegal or Johannesburg in South Africa), and
                lacks the administrative and political status that typically
                attracts disproportionate public investment. What Secondary
                Cities do have is growth potential and major challenges that
                stand between them and the investments needed to attract the
                financial and human capital they require.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Why are Secondary Cities so important to AIR Fund?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                Much of existing investment in Africa tends to go to major
                economic hubs like Lagos, Nairobi, Abidjan, and Johannesburg
                that are already economic nerve centers that attract both talent
                and funds. At AIR Fund, we believe that this has to change if
                Africa is to unlock a new level of growth and development. By
                identifying and investing in new growth centers outside of
                existing capitals and economic hubs, we can unlock unrealized
                economic growth potential, boost employment and offer exciting
                new opportunities for economic growth and prosperity. Read our
                Theory of Change{" "}
                <Link
                  href={"/the-fund"}
                  paddingTop={{ xs: "6%", sm: "4%" }}
                  underline="hover"
                  sx={{
                    color: "#948979",
                    "&:hover": {
                      color: "#AC7551",
                    },
                  }}
                >
                  here
                </Link>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
          >
            <AccordionSummary
              aria-controls="panel3d-content"
              id="panel3d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Do you have specific countries in Africa where one must apply
                from?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                Selected countries will be announced with each grant cycle.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
          >
            <AccordionSummary
              aria-controls="panel4d-content"
              id="panel4d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                I would like to be considered for an AIR Fund grant. Do I need
                to be based in a Secondary City?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                The grantee need not be based in a secondary city, but their
                work must contribute directly to the economic growth and
                development of secondary cities. Read more about who we fund{" "}
                <Link
                  href={"/for-grantees"}
                  paddingTop={{ xs: "6%", sm: "4%" }}
                  underline="hover"
                  sx={{
                    color: "#948979",
                    "&:hover": {
                      color: "#AC7551",
                    },
                  }}
                >
                  here
                </Link>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel5"}
            onChange={handleChange("panel5")}
          >
            <AccordionSummary
              aria-controls="panel5d-content"
              id="panel5d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                How do I know that my project qualifies for an AIR Fund grant?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                You can read our full list of requirements{" "}
                <Link
                  href={"/for-grantees"}
                  paddingTop={{ xs: "6%", sm: "4%" }}
                  underline="hover"
                  sx={{
                    color: "#948979",
                    "&:hover": {
                      color: "#AC7551",
                    },
                  }}
                >
                  here
                </Link>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel6"}
            onChange={handleChange("panel6")}
          >
            <AccordionSummary
              aria-controls="panel6d-content"
              id="panel6d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Will you have a new set of grantees every year?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                The current plan is to offer grants every other year.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel7"}
            onChange={handleChange("panel7")}
          >
            <AccordionSummary
              aria-controls="panel7d-content"
              id="panel7d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                What themes does my project need to cover to qualify for an AIR
                Fund grant?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                This first round of AIR Fund grants will be for projects that
                address climate resilience, use artificial intelligence, and/or
                introduce other innovative approaches to urban development
                challenges in Secondary Cities.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel8"}
            onChange={handleChange("panel8")}
          >
            <AccordionSummary
              aria-controls="panel8d-content"
              id="panel8d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Your website says that you provide grants of US$150,000/year for
                two years. Will you consider investing for more than two years?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                There are currently no plans to do so, but we will provide
                updates if that changes.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel9"}
            onChange={handleChange("panel9")}
          >
            <AccordionSummary
              aria-controls="panel9d-content"
              id="panel9d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                I have an idea for a project that my organization has never
                implemented before. Could I be considered for an AIR Fund grant?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                Your entity needs to have implemented the idea at a small scale
                and/or have proof of concept. Read the full list of requirements{" "}
                <Link
                  href={"/for-grantees"}
                  paddingTop={{ xs: "6%", sm: "4%" }}
                  underline="hover"
                  sx={{
                    color: "#948979",
                    "&:hover": {
                      color: "#AC7551",
                    },
                  }}
                >
                  here
                </Link>
                .
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel10"}
            onChange={handleChange("panel10")}
          >
            <AccordionSummary
              aria-controls="panel10d-content"
              id="panel10d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Do I have to be a nonprofit to apply for AIR Fund?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                No. Any organization – for profit or not – can apply as long as
                your project meets the criteria.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel11"}
            onChange={handleChange("panel11")}
          >
            <AccordionSummary
              aria-controls="panel11d-content"
              id="panel11d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                What would AIR Fund grants cover?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                The funds are restricted to project activities aimed at
                improving product/project development and market acceptance.
                They will not cover conference fees or travel.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel12"}
            onChange={handleChange("panel12")}
          >
            <AccordionSummary
              aria-controls="panel12d-content"
              id="panel12d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Does AIR Fund seek ownership of the organizations it supports?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                No. Investments made by the AIR Fund are equity-free.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel13"}
            onChange={handleChange("panel13")}
          >
            <AccordionSummary
              aria-controls="panel13d-content"
              id="panel13d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                My project does not explicitly address the needs of underserved
                populations. Does that disqualify me from an AIR Fund grant?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                Not necessarily, but AIR Fund will prioritize projects that
                address the needs of underserved populations like women, youth,
                and people with disabilities.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel14"}
            onChange={handleChange("panel14")}
          >
            <AccordionSummary
              aria-controls="panel14d-content"
              id="panel14d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                What information should I incorporate in my proposal for
                participating in the funding cycle?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                The guiding template for proposal development and required
                periodic deliverables will be released during the particular
                funding cycle.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
}
